import React, { useState } from "react";
import { Play, ShieldCheck, Sparkles, ChevronRight } from "lucide-react";
import { api } from "../../api";
import { dishPhoto, dishVideo, ytId, splitBadges, CommercialBadge, AttributePill, PhotoFallback } from "./shared";

export default function DishDetail({ d, label, showVideo, setShowVideo, dishById, onOpenSuggested }) {
  const photos = (d.media || []).filter((x) => x.kind === "image");
  const video = dishVideo(d);
  const yt = ytId(d.external_video);
  const [gallery, setGallery] = useState(0);
  const hasVisual = photos.length > 0 || video || yt;
  const { commercial, attrs } = splitBadges(d.badges);
  const allergens = d.allergen_status === "confermato" ? (d.allergens_confirmed || []) : [];
  const suggested = (d.suggested || []).map((sid) => dishById[sid]).filter(Boolean);

  return (
    <div>
      {hasVisual ? (
        <div className="relative bg-black aspect-square">
          {showVideo && video ? (
            <video src={api.mediaUrl(video.id)} controls autoPlay playsInline className="h-full w-full object-contain bg-black" data-testid="dish-video-player" />
          ) : showVideo && yt ? (
            <iframe title="video" src={`https://www.youtube.com/embed/${yt}?autoplay=1`} className="h-full w-full" allow="autoplay; encrypted-media" allowFullScreen />
          ) : photos.length > 0 ? (
            <img src={api.mediaUrl(photos[gallery].id, 800)} alt={d.name} className="h-full w-full object-cover" />
          ) : (
            <PhotoFallback className="h-full w-full" iconClass="h-12 w-12" />
          )}
          {!showVideo && (video || yt) && (
            <button onClick={() => setShowVideo(true)} data-testid="dish-play-video" className="absolute inset-0 grid place-items-center bg-black/25">
              <span className="h-16 w-16 rounded-full bg-white/95 grid place-items-center shadow-lg"><Play className="h-7 w-7 text-[#123C46] fill-[#123C46] ml-1" /></span>
              <span className="absolute bottom-4 text-white text-xs font-semibold bg-black/50 px-3 py-1 rounded-full">Guarda il video</span>
            </button>
          )}
          {commercial.length > 0 && !showVideo && (
            <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 max-w-[85%]">{commercial.map((b) => <CommercialBadge key={b} code={b} />)}</div>
          )}
        </div>
      ) : (
        <PhotoFallback className="h-24" iconClass="h-8 w-8" />
      )}

      {!showVideo && photos.length > 1 && (
        <div className="flex gap-2 p-3 overflow-x-auto no-scrollbar">
          {photos.map((p, i) => (
            <button key={p.id} onClick={() => setGallery(i)} className={`h-14 w-14 rounded-lg overflow-hidden shrink-0 border-2 ${i === gallery ? "border-[#123C46]" : "border-transparent"}`}>
              <img src={api.mediaUrl(p.id, 160)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="p-5">
        {!hasVisual && commercial.length > 0 && <div className="flex flex-wrap gap-1.5 mb-3">{commercial.map((b) => <CommercialBadge key={b} code={b} />)}</div>}
        <div className="flex justify-between gap-3 items-start">
          <h2 className="font-display text-2xl font-extrabold text-[#0c2d34] leading-tight break-words min-w-0">{d.name}</h2>
          <span className="font-bold text-lg text-[#123C46] whitespace-nowrap">{d.price}</span>
        </div>
        {attrs.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{attrs.map((b) => <AttributePill key={b} code={b} />)}</div>}
        {d.description && <p className="text-[15px] text-[#3f5b60] mt-2.5 leading-relaxed">{d.description}</p>}

        {d.note && (
          <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
            <span className="font-semibold">Nota del locale: </span>{d.note}
          </div>
        )}

        {(d.variants || []).length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Varianti</p>
            <div className="rounded-xl border border-border divide-y divide-border overflow-hidden">
              {d.variants.map((v, i) => <div key={i} className="flex justify-between text-sm px-3 py-2 bg-white"><span>{v.name}</span><span className="font-semibold text-[#123C46]">{v.price}</span></div>)}
            </div>
          </div>
        )}

        {d.ingredients && (
          <div className="mt-4">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Ingredienti</p>
            <p className="text-sm text-[#3f5b60] leading-relaxed">{d.ingredients}</p>
          </div>
        )}

        {d.pairing && (
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#123C46]/5 border border-[#123C46]/10 px-3 py-2.5">
            <Sparkles className="h-4 w-4 text-[#123C46] mt-0.5 shrink-0" />
            <div><p className="text-xs font-bold text-[#123C46]">Abbinamento consigliato</p><p className="text-sm text-[#3f5b60]">{d.pairing}</p></div>
          </div>
        )}

        {allergens.length > 0 && (
          <div className="mt-4 rounded-xl border border-dashed border-emerald-300/70 bg-emerald-50/40 px-3 py-2.5" data-testid="detail-allergens">
            <p className="text-xs font-semibold flex items-center gap-1 text-emerald-700 mb-1"><ShieldCheck className="h-3.5 w-3.5" /> Allergeni confermati dal gestore</p>
            <p className="text-sm text-[#3f5b60] leading-relaxed">{allergens.map(label).join(" · ")}</p>
          </div>
        )}

        {suggested.length > 0 && (
          <div className="mt-5 border-t border-border pt-4">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Ti potrebbe piacere</p>
            <div className="space-y-2">
              {suggested.map((sd) => {
                const sp = dishPhoto(sd, 160);
                return (
                  <button key={sd.id} onClick={() => onOpenSuggested(sd)} data-testid={`suggested-${sd.id}`}
                    className="w-full flex items-center gap-3 rounded-xl border border-border p-2 hover:bg-muted/40 text-left">
                    {sp ? <img src={sp} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" loading="lazy" />
                      : <PhotoFallback className="h-12 w-12 rounded-lg shrink-0" iconClass="h-5 w-5" />}
                    <div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{sd.name}</p><p className="text-xs text-muted-foreground">{sd.price}</p></div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
